import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useSelector } from "react-redux";
import { MAIN_API } from "../../api/apilist";
import { beautyUpdateSalonApi } from "../../api/organizerBeautyApi";
import OrganizerTimePillSelect from "../components/OrganizerTimePillSelect";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import OnlineBookingSwitch from "../components/OnlineBookingSwitch";
import ClubAddressAutocomplete from "../canchas/ClubAddressAutocomplete";
import ClubLocationPicker from "../canchas/ClubLocationPicker";
import useCitiesByState from "../../hooks/useCitiesByState";
import { COUNTRIES } from "../../pages/reservas/components/ReservaShared";
import {
  camposDeCliente,
  datosDeSalon,
  faltaSalonEditado,
  formDataActualizarSalon,
} from "./primerUso/salonPayload";
import ClientFieldsEditor from "./ClientFieldsEditor";
import ComisionesTab from "./ComisionesTab";
import ProfesionalesTab from "./ProfesionalesTab";
import ServiciosTab from "./ServiciosTab";
import { OrgToast } from "./TurnosUi";
import { BarraAccionesContext } from "./barraAcciones";
import RecordatoriosAjustes from "../recordatorios/RecordatoriosAjustes";
import PedidoResenaAjustes from "../recordatorios/PedidoResenaAjustes";
import ListaEsperaAjustes from "../recordatorios/ListaEsperaAjustes";
import { AvisoBorrador, borrarBorrador, useBorradorPoliticas } from "../recordatorios/borradorPoliticas";
import "../reservas/reservas-organizer.css";
import "./turnos-beauty.css";

/* ─────────────────────────────────────────
   SalonForm — editor del salón con el mismo patrón visual que ClubForm (Canchas):
   secciones "info", "ubicacion", "horarios", "politicas" usando clases zr-*,
   más pestañas "servicios" y "profesionales" del rubro Beauty.
───────────────────────────────────────── */

const WEEK_DAYS = [
  { weekday: 0, label: "Domingo" },
  { weekday: 1, label: "Lunes" },
  { weekday: 2, label: "Martes" },
  { weekday: 3, label: "Miércoles" },
  { weekday: 4, label: "Jueves" },
  { weekday: 5, label: "Viernes" },
  { weekday: 6, label: "Sábado" },
];

const SECCIONES = [
  { id: "info", label: "INFORMACIÓN GENERAL" },
  { id: "ubicacion", label: "UBICACIÓN" },
  { id: "horarios", label: "HORARIOS" },
  { id: "politicas", label: "POLÍTICAS" },
  { id: "servicios", label: "SERVICIOS", propio: true },
  { id: "profesionales", label: "PROFESIONALES", propio: true },
  { id: "comisiones", label: "COMISIONES", propio: true, soloDueno: true },
  { id: "clientes", label: "FICHA DE CLIENTE", soloDueno: true },
];

const MAPEO_SECCION = {
  contacto: "info",
  fotos: "info",
};

const MIN_ADVANCE_OPTIONS = [
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hora" },
  { value: 120, label: "2 horas" },
  { value: 240, label: "4 horas" },
  { value: 480, label: "8 horas" },
  { value: 1440, label: "1 día" },
];

const CANCELLATION_HOURS_OPTIONS = [
  { value: 1, label: "1 hora antes" },
  { value: 2, label: "2 horas antes" },
  { value: 6, label: "6 horas antes" },
  { value: 12, label: "12 horas antes" },
  { value: 24, label: "24 horas antes" },
  { value: 48, label: "48 horas antes" },
];

const RESCHEDULE_MAX_OPTIONS = [1, 2, 3].map((n) => ({ value: n, label: n === 1 ? "1 vez" : `${n} veces` }));

const RESERVATION_WINDOW_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1).map((d) => ({
  value: d,
  label: `${d} ${d === 1 ? "día" : "días"}`,
}));

const TOLERANCE_OPTIONS = [5, 10, 15, 20, 30].map((v) => ({
  value: v,
  label: `${v} min`,
}));

const resolveUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (/^blob:/i.test(u)) return u;
  if (u.startsWith("/")) return `${MAIN_API}${u}`;
  return `${MAIN_API}/${u}`;
};

function calculateHours(open, close) {
  if (!open || !close) return 0;
  const [openH, openM] = open.split(":").map(Number);
  const [closeH, closeM] = close.split(":").map(Number);
  let openMin = openH * 60 + openM;
  let closeMin = closeH * 60 + closeM;
  if (closeMin <= openMin) closeMin += 24 * 60;
  return (closeMin - openMin) / 60;
}

const SalonForm = ({ salon, states = [], onSaved, onSalonChange, onVolver, initialSection = "info" }) => {
  const esEmpleado = useSelector((s) => s.auth)?.user?.tipo === "empleado";
  // Un empleado no ve las pestañas de dueño: todo índice de sección va contra esta lista.
  const secciones = useMemo(() => SECCIONES.filter((s) => !(s.soloDueno && esEmpleado)), [esEmpleado]);
  const [data, setData] = useState(() => datosDeSalon(salon));
  const [original, setOriginal] = useState(data);
  const [seccion, setSeccion] = useState(() => {
    const targetId = MAPEO_SECCION[initialSection] || initialSection;
    const idx = secciones.findIndex((s) => s.id === targetId);
    return idx >= 0 ? idx : 0;
  });
  const [saving, setSaving] = useState(false);
  const [slotAcciones, setSlotAcciones] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const { cities } = useCitiesByState(data.stateid);
  const countryRef = useRef(null);
  const fileInputRef = useRef(null);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [dragTarget, setDragTarget] = useState("");

  const set = useCallback((patch) => setData((prev) => ({ ...prev, ...patch })), []);
  const cerrarToast = useCallback(() => setToast(""), []);
  // Edits made before leaving for Zoco Chat to manage WhatsApp templates come back unsaved.
  const borrador = useBorradorPoliticas({ id: salon?._id, form: data, restaurar: set });

  useEffect(() => {
    const handleDocClick = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, []);

  const selectedCountry = COUNTRIES.find((c) => c.prefix === data.prefijo) || COUNTRIES[0];

  const handleCountryChange = (c) => {
    set({ prefijo: c.prefix });
    setCountryDropdownOpen(false);
  };

  const createDropHandlers = (key, onFile) => ({
    onDragEnter: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragTarget(key);
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragTarget(key);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setDragTarget((prev) => (prev === key ? "" : prev));
    },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragTarget((prev) => (prev === key ? "" : prev));
      const file = e.dataTransfer?.files?.[0] || null;
      if (file) onFile(file);
    },
  });

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file) set({ portada: file });
  };

  const handleClearCover = (e) => {
    e.stopPropagation();
    set({ portada: null });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const setDia = (weekday, patch) =>
    setData((prev) => ({
      ...prev,
      horarios: prev.horarios.map((h) => (h.weekday === weekday ? { ...h, ...patch } : h)),
    }));

  const guardar = async () => {
    const falta = faltaSalonEditado(data, original);
    if (falta) {
      setError(falta);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await beautyUpdateSalonApi(formDataActualizarSalon(salon._id, data, original));
      const actualizado = res?.data?.info || null;
      // New field definitions get their id from the server: take the saved list back, or a second save would duplicate them.
      const camposGuardados = Array.isArray(actualizado?.clientFields) ? camposDeCliente(actualizado.clientFields) : data.clientFields;
      const guardado = { ...data, clientFields: camposGuardados, portada: null, galeria: [] };
      setData(guardado);
      setOriginal(guardado);
      borrarBorrador(salon._id);
      setToast("Cambios guardados");
      if (onSaved) onSaved(actualizado);
    } catch (e) {
      setError(e?.response?.data?.message || "No pudimos guardar los cambios. Probá de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const id = secciones[seccion]?.id;

  return (
    <div style={{ paddingBottom: 60 }}>
      <AvisoBorrador visible={borrador.recuperado} />
      {error && (
        <div
          role="alert"
          style={{
            background: "#fdecea",
            border: "1px solid #f8b4b4",
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 24,
            color: "#e53935",
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "pre-line",
          }}
        >
          {error}
        </div>
      )}

      <nav className="zr-tabs">
        {secciones.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            className={"zr-tabs__item" + (seccion === idx ? " zr-tabs__item--active" : "")}
            onClick={() => setSeccion(idx)}
          >
            {s.label}
          </button>
        ))}
      </nav>


      {/* ─── 0. INFORMACIÓN GENERAL ─── */}
      {id === "info" && (
        <section id="salon-sec-info">
          <div className="zr-panel">
            <div className="zr-section-head">
              <h2 className="zr-section-head__title">Información del salón</h2>
              <p className="zr-section-head__hint">| Los datos principales que verán tus clientes.</p>
            </div>
            <Row className="g-4">
              <Col md={6} className="d-flex flex-column gap-4">
                <div className="zr-field">
                  <span className="zr-field__label">Nombre del salón</span>
                  <input
                    className="zr-input"
                    value={data.nombre}
                    onChange={(e) => set({ nombre: e.target.value })}
                    placeholder="Ej: Estudio Luna"
                  />
                </div>
                <div className="zr-field" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <span className="zr-field__label">Descripción</span>
                  <textarea
                    className="zr-textarea"
                    style={{ height: 228, minHeight: 228, flex: 1 }}
                    value={data.descripcion}
                    onChange={(e) => set({ descripcion: e.target.value })}
                    placeholder="Contá más sobre el salón..."
                  />
                </div>
              </Col>
              <Col md={6} className="d-flex flex-column gap-4">
                <div className="zr-field">
                  <span className="zr-field__label">Teléfono de contacto</span>
                  <div
                    ref={countryRef}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid var(--zr-ink)",
                      borderRadius: "var(--zr-radius)",
                      height: "40px",
                      background: "#fff",
                      position: "relative",
                      boxSizing: "border-box",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        paddingLeft: "14px",
                        paddingRight: "12px",
                        height: "100%",
                        background: "#f9fafb",
                        borderTopLeftRadius: "9px",
                        borderBottomLeftRadius: "9px",
                        userSelect: "none",
                        borderRight: "1px solid #eaedf3",
                        flexShrink: 0,
                        cursor: "pointer",
                        position: "relative",
                      }}
                      onClick={() => setCountryDropdownOpen((o) => !o)}
                    >
                      {selectedCountry.flag}
                      <span style={{ fontSize: "13.5px", fontWeight: "700", color: "#2d3035", whiteSpace: "nowrap" }}>
                        {selectedCountry.label}
                      </span>
                      <svg
                        width="8"
                        height="5"
                        viewBox="0 0 8 5"
                        fill="none"
                        style={{
                          marginLeft: "2px",
                          transform: countryDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      >
                        <path d="M1 1L4 4L7 1" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {countryDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            left: 0,
                            background: "#fff",
                            border: "1px solid #E5E7EB",
                            borderRadius: "10px",
                            boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                            zIndex: 1000,
                            minWidth: "160px",
                            padding: "6px 0",
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          {COUNTRIES.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCountryChange(c);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                padding: "8px 14px",
                                border: "none",
                                background: selectedCountry.code === c.code ? "#f3f4f6" : "#fff",
                                cursor: "pointer",
                                gap: "10px",
                                textAlign: "left",
                              }}
                            >
                              {c.flag}
                              <span style={{ fontSize: "13px", fontWeight: "600", color: "#2d3035", flex: 1 }}>{c.label}</span>
                              {selectedCountry.code === c.code && <span style={{ color: "#00b159", fontWeight: "bold", fontSize: "14px" }}>✓</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      placeholder={selectedCountry.placeholder}
                      style={{
                        border: "none",
                        outline: "none",
                        height: "100%",
                        width: "100%",
                        paddingLeft: "12px",
                        paddingRight: "14px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "var(--zr-ink)",
                        background: "transparent",
                      }}
                      value={data.telefono}
                      onChange={(e) => set({ telefono: e.target.value })}
                    />
                  </div>
                </div>
                <div className="zr-field">
                  <span className="zr-field__label">Email de contacto</span>
                  <input
                    type="email"
                    className="zr-input"
                    value={data.email}
                    onChange={(e) => set({ email: e.target.value })}
                    placeholder="salon@ejemplo.com"
                  />
                </div>
                <div className="zr-field">
                  <span className="zr-field__label">Sitio web (opcional)</span>
                  <input
                    type="url"
                    className="zr-input"
                    value={data.web}
                    onChange={(e) => set({ web: e.target.value })}
                    placeholder="https://www.tu-salon.com"
                  />
                </div>
                <div className="zr-field">
                  <span className="zr-field__label">Instagram (opcional - solo nombre de usuario)</span>
                  <input
                    type="text"
                    className="zr-input"
                    value={data.instagram}
                    onChange={(e) => set({ instagram: e.target.value })}
                    placeholder="nombre_de_usuario"
                  />
                </div>
              </Col>
            </Row>

            {/* Imagen de portada */}
            <div className="zr-field" style={{ marginTop: 28 }}>
              <span className="zr-field__label">
                Imagen de portada{" "}
                <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "12px", marginLeft: "6px" }}>
                  (Recomendado: 1600 x 900 px)
                </span>
              </span>
              <div
                className="zr-upload"
                style={{
                  height: 220,
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  border: dragTarget === "cover" ? "2px dashed var(--zr-lime)" : undefined,
                }}
                onClick={() => fileInputRef.current?.click()}
                {...createDropHandlers("cover", (file) => set({ portada: file }))}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />
                {data.portada || salon?.image ? (
                  <>
                    <img
                      src={data.portada ? URL.createObjectURL(data.portada) : resolveUrl(salon?.image)}
                      alt="Portada"
                      style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
                    />
                    <button
                      type="button"
                      aria-label="Quitar imagen"
                      onClick={handleClearCover}
                      className="zr-delete-img-btn zr-delete-img-btn--cover"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
                      </svg>
                    </button>
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" width="28" height="24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span className="t">Subir imagen de portada</span>
                    <span className="h">Arrastrá o seleccioná un archivo JPG / PNG</span>
                  </>
                )}
              </div>
            </div>

            {/* Galería de fotos */}
            <div className="zr-field" style={{ marginTop: 24 }}>
              <span className="zr-field__label">
                Galería de fotos | 5 máx.{" "}
                <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "12px", marginLeft: "6px" }}>
                  (Recomendado: 1080 x 1080 px)
                </span>
              </span>
              <div className="zr-gallery">
                {(salon?.galleryimage || []).map((url, idx) => (
                  <div key={`existing-${idx}`} className="zr-gallery__slot" style={{ position: "relative", overflow: "hidden" }}>
                    <img src={resolveUrl(url)} alt="Galería existente" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                ))}
                {(data.galeria || []).map((file, idx) => (
                  <div key={`new-${idx}`} className="zr-gallery__slot" style={{ position: "relative", overflow: "hidden" }}>
                    <img src={URL.createObjectURL(file)} alt="Nueva foto" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button
                      type="button"
                      aria-label="Quitar foto"
                      onClick={() => set({ galeria: data.galeria.filter((_, i) => i !== idx) })}
                      className="zr-delete-img-btn zr-delete-img-btn--gallery"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
                      </svg>
                    </button>
                  </div>
                ))}
                {((salon?.galleryimage?.length || 0) + (data.galeria?.length || 0)) < 5 && (
                  <label className="zr-gallery__add">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    <span>Subir Foto</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const allowedCount = 5 - ((salon?.galleryimage?.length || 0) + (data.galeria?.length || 0));
                        set({ galeria: [...(data.galeria || []), ...files.slice(0, allowedCount)] });
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── 1. UBICACIÓN ─── */}
      {id === "ubicacion" && (
        <section id="salon-sec-ubicacion">
          <div className="zr-panel">
            <div className="zr-section-head">
              <h2 className="zr-section-head__title">Ubicación</h2>
              <p className="zr-section-head__hint">| Dónde está tu salón. Aparecerá en los filtros de búsqueda.</p>
            </div>
            <Row className="g-4">
              <Col md={6}>
                <div className="zr-field">
                  <span className="zr-field__label">Provincia</span>
                  <OrganizerPillSelect
                    placeholder="Sin provincia"
                    value={data.stateid}
                    onChange={(val) => set({ stateid: val, cityid: "" })}
                    options={[{ value: "", label: "Sin provincia" }, ...states.map((s) => ({ value: s._id, label: s.name }))]}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="zr-field">
                  <span className="zr-field__label">Ciudad</span>
                  <OrganizerPillSelect
                    placeholder="Sin ciudad"
                    value={data.cityid}
                    searchable
                    disabled={!data.stateid}
                    onChange={(val) => set({ cityid: val })}
                    options={[{ value: "", label: "Sin ciudad" }, ...cities.map((c) => ({ value: c._id, label: c.name }))]}
                  />
                </div>
              </Col>
              <Col md={12}>
                <div className="zr-field">
                  <span className="zr-field__label">Dirección</span>
                  <ClubAddressAutocomplete
                    value={data.address}
                    onChange={(val) => set({ address: val, lat: "", lng: "" })}
                    onPlaceSelected={({ address, lat, lng }) => set({ address, lat, lng })}
                  />
                </div>
              </Col>
            </Row>
            <div className="zr-field" style={{ marginTop: 24 }}>
              <span className="zr-field__label">Ubicación en el mapa</span>
              <span className="zr-field__hint">
                Buscamos la dirección automáticamente. Si el pin no cae en el lugar exacto, arrastralo o hacé clic en el mapa para corregirlo.
              </span>
              <div className="zr-map">
                {(() => {
                  const cityName = cities.find((c) => String(c._id) === String(data.cityid))?.name || "";
                  const stateName = states.find((s) => String(s._id) === String(data.stateid))?.name || "";
                  const queryAddress = [data.address, cityName, stateName].filter(Boolean).join(", ");
                  return (
                    <ClubLocationPicker
                      address={queryAddress}
                      lat={data.lat}
                      lng={data.lng}
                      entityLabel="salón"
                      onChange={({ lat, lng }) => set({ lat, lng })}
                    />
                  );
                })()}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── 2. HORARIOS ─── */}
      {id === "horarios" && (
        <section id="salon-sec-horarios">
          <Row className="g-4 align-items-start">
            <Col lg={6}>
              <div className="zr-panel">
                <div className="zr-section-head">
                  <h2 className="zr-section-head__title">Horarios de atención</h2>
                  <p className="zr-section-head__hint">| Activa los días que abrís y definí la franja horaria de cada uno.</p>
                </div>
                {WEEK_DAYS.map((d) => {
                  const row = data.horarios.find((h) => h.weekday === d.weekday) || { weekday: d.weekday, enabled: false, open: "09:00", close: "20:00" };
                  return (
                    <div className="zr-day-row" key={d.weekday}>
                      <span className="zr-day-row__name">{d.label}</span>
                      <input
                        type="checkbox"
                        className="zr-switch"
                        aria-label={d.label}
                        checked={row.enabled}
                        onChange={() => setDia(d.weekday, { enabled: !row.enabled })}
                      />
                      <OrganizerTimePillSelect
                        value={row.open}
                        onChange={(val) => setDia(d.weekday, { open: val })}
                        disabled={!row.enabled}
                        quarterHours
                        triggerClassName="zr-day-row__time"
                        ariaLabel={`Abre el ${d.label}`}
                      />
                      <OrganizerTimePillSelect
                        value={row.close}
                        onChange={(val) => setDia(d.weekday, { close: val })}
                        disabled={!row.enabled}
                        quarterHours
                        triggerClassName="zr-day-row__time"
                        ariaLabel={`Cierra el ${d.label}`}
                      />
                    </div>
                  );
                })}
                <p className="ef-footnote" style={{ marginTop: 16 }}>
                  Es el horario del salón. Cada profesional puede tener el suyo en Profesionales.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="zr-panel">
                {(() => {
                  const totalHours = data.horarios
                    .filter((h) => h.enabled)
                    .reduce((sum, h) => sum + calculateHours(h.open, h.close), 0);
                  return (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <h2 className="zr-section-head__title">Resúmen semanal</h2>
                      <span className="zr-section-head__hint" style={{ margin: 0 }}>
                        <b>{totalHours}</b> hs en total
                      </span>
                    </div>
                  );
                })()}
                <div className="zr-week">
                  {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((label, idx) => {
                    const row = data.horarios.find((h) => h.weekday === idx);
                    const on = row?.enabled;
                    const hrs = on ? calculateHours(row.open, row.close) : 0;
                    return (
                      <div key={idx} className={"zr-week__day" + (on ? " zr-week__day--on" : "")}>
                        <span>{label}</span>
                        {on && (
                          <span className="hrs">
                            {hrs} hs<br />al día
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Col>
          </Row>
        </section>
      )}

      {/* ─── 3. POLÍTICAS ─── */}
      {id === "politicas" && (
        <section id="salon-sec-politicas">
          <div className="zr-panel">
            <div className="zr-section-head">
              <h2 className="zr-section-head__title">Políticas de reserva</h2>
              <p className="zr-section-head__hint">| Las reglas para reservar y cancelar. La seña se define en cada servicio.</p>
            </div>
            <OnlineBookingSwitch
              checked={data.onlineBookingEnabled}
              onChange={(onlineBookingEnabled) => set({ onlineBookingEnabled })}
              disabled={saving}
            />
            <Row className="g-4">
              <Col md={6}>
                <div className="zr-field">
                  <span className="zr-field__label">Ventana de reserva</span>
                  <OrganizerPillSelect
                    value={data.ventana}
                    onChange={(val) => set({ ventana: Number(val) })}
                    options={RESERVATION_WINDOW_OPTIONS}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="zr-field">
                  <span className="zr-field__label">Anticipación mínima</span>
                  <OrganizerPillSelect
                    value={data.antelacion}
                    onChange={(val) => set({ antelacion: Number(val) })}
                    options={MIN_ADVANCE_OPTIONS}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="zr-field">
                  <span className="zr-field__label">Tolerancia de llegada</span>
                  <OrganizerPillSelect
                    value={data.tolerancia}
                    onChange={(val) => set({ tolerancia: Number(val) })}
                    options={TOLERANCE_OPTIONS}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="zr-field">
                  <span className="zr-field__label">¿Se permite cancelar?</span>
                  <input
                    type="checkbox"
                    className="zr-switch"
                    aria-label="Cancelación de reserva"
                    checked={data.cancela}
                    onChange={() => set({ cancela: !data.cancela })}
                  />
                </div>
              </Col>
              {data.cancela && (
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Plazo de cancelación</span>
                    <OrganizerPillSelect
                      value={data.plazo}
                      onChange={(val) => set({ plazo: Number(val) })}
                      options={CANCELLATION_HOURS_OPTIONS}
                    />
                  </div>
                </Col>
              )}
              <Col md={6}>
                <div className="zr-field">
                  <span className="zr-field__label">Permitir reagendar</span>
                  <input
                    type="checkbox"
                    className="zr-switch"
                    aria-label="Permitir reagendar"
                    checked={data.reagendar}
                    onChange={() => set({ reagendar: !data.reagendar })}
                  />
                </div>
              </Col>
              {data.reagendar && (
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Máximo por turno</span>
                    <OrganizerPillSelect
                      value={data.reagendarMax}
                      onChange={(val) => set({ reagendarMax: Number(val) })}
                      options={RESCHEDULE_MAX_OPTIONS}
                    />
                    <small style={{ color: "#6b7280", fontSize: 12 }}>El límite de tiempo es el mismo que el de cancelación</small>
                  </div>
                </Col>
              )}
              {/* Solo el dueño los configura: `enabled` vale para todos los canales (email y WhatsApp). */}
              {!esEmpleado && (
                <Col md={12}>
                  <RecordatoriosAjustes
                    activos={data.recordatoriosActivos}
                    horasAntes={data.horasAntesRecordatorio}
                    onChange={(campo, valor) => set({ [campo]: valor })}
                    whatsapp={data.whatsapp}
                    onWhatsappChange={(whatsapp) => set({ whatsapp })}
                    push={data.push}
                    onPushChange={(push) => set({ push })}
                    sustantivo="turno"
                    local={data.nombre}
                    onAntesDeIrAChat={borrador.guardar}
                    volver={borrador.volver}
                  />
                </Col>
              )}
              {!esEmpleado && (
                <Col md={12}>
                  <PedidoResenaAjustes
                    resena={data.resena}
                    onChange={(resena) => set({ resena })}
                    local={data.nombre}
                    onAntesDeIrAChat={borrador.guardar}
                    volver={borrador.volver}
                  />
                </Col>
              )}
              {!esEmpleado && (
                <Col md={12}>
                  <ListaEsperaAjustes
                    lista={data.listaEspera}
                    onChange={(listaEspera) => set({ listaEspera })}
                    local={data.nombre}
                    onAntesDeIrAChat={borrador.guardar}
                    volver={borrador.volver}
                  />
                </Col>
              )}
              <Col md={12}>
                <div className="zr-field">
                  <span className="zr-field__label">Notas adicionales (opcional)</span>
                  <textarea
                    className="zr-textarea"
                    value={data.notas}
                    onChange={(e) => set({ notas: e.target.value })}
                    placeholder="Información extra para el cliente al reservar"
                  />
                </div>
              </Col>
            </Row>
          </div>
        </section>
      )}

      {/* ─── 7. FICHA DE CLIENTE: definiciones de campos propios (solo dueño) ─── */}
      {id === "clientes" && <ClientFieldsEditor fields={data.clientFields} onChange={(clientFields) => set({ clientFields })} />}

      {/* ─── 4. SERVICIOS (propio de Beauty) ─── */}
      {id === "servicios" && <ServiciosTab salon={salon} onSalonChange={onSalonChange} />}

      {/* ─── 5. PROFESIONALES (propio de Beauty) ─── */}
      {id === "profesionales" && (
        <BarraAccionesContext.Provider value={slotAcciones}>
          <ProfesionalesTab
            salon={salon}
            onSalonChange={onSalonChange}
            onIrAServicios={() => setSeccion(secciones.findIndex((x) => x.id === "servicios"))}
          />
        </BarraAccionesContext.Provider>
      )}

      {/* ─── 6. COMISIONES (propio de Beauty, solo dueño) ─── */}
      {id === "comisiones" && (
        <BarraAccionesContext.Provider value={slotAcciones}>
          <ComisionesTab salon={salon} />
        </BarraAccionesContext.Provider>
      )}

      <div className="zr-formfooter zr-formfooter--sticky-bottom">
        <span className="zr-formfooter__status">
          <span className="dot" />
          <span className="main-status">Editando {original.nombre || "salón"}</span>
        </span>
        <div className="zr-formfooter__actions">
          {onVolver && (
            <button type="button" className="zr-btn zr-btn--ghost" onClick={onVolver}>
              Volver
            </button>
          )}
          <span ref={setSlotAcciones} className="zr-formfooter__slot" />
          {!secciones[seccion]?.propio && (
            <button type="button" className="zr-btn zr-btn--dark" onClick={guardar} disabled={saving}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          )}
        </div>
      </div>

      <OrgToast message={toast} onClose={cerrarToast} />
    </div>
  );
};

export default SalonForm;
